/**
 * Footer-save dismissal decision (Multi-Add Phase 2).
 *
 * Pure helper extracted from `wiring.ts`'s `handleCommit` so the
 * decision table is unit-testable without dragging in the entire
 * settings-store + agent-broker hook tree.
 *
 * Per `think-in-systems` (ai.knowledge/global): the React adapter
 * (`useWorkflow.handleCommit`) calls this pure helper at the start of
 * the commit flow; the dismissal write is the side-effect on the
 * non-null return.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 *
 * Dismissal semantics:
 *   - Global Save WITH edits to the active tab's surface = user
 *     acknowledgement of that tab's warning.
 *   - Save WITHOUT relevant edits does NOT dismiss anything (avoids
 *     hiding the warning while the underlying problem is unresolved).
 *
 * Dirty-flag fallback: the settings store doesn't carry per-tab dirty
 * trackers — only the high-level `isDeviceSettingsDirty` /
 * `isVideoSettingsDirty` flags. Mapping:
 *   - video sub-tab + isVideoSettingsDirty → dismiss 'video'
 *   - control sub-tab + isDeviceSettingsDirty → dismiss 'control'
 *   - driver sub-tab + isDeviceSettingsDirty → dismiss 'driver'
 *
 * Authentication is dismissed by its OWN local Submit
 * (`authentication-content.tsx`), NOT by the global Save. Monitoring
 * is out of scope entirely.
 */

/** Sub-tab keys that can be the footer-save dismissal target. */
type FooterDismissalTab = 'video' | 'control' | 'driver';

/** Active sub-tab argument — includes the out-of-scope ones for completeness. */
type IrisConnectionTab = 'monitoring' | 'authentication' | 'video' | 'control' | 'driver';

/**
 * Derive the footer-save dismissal tab from the active Iris-
 * Connection sub-tab + the high-level settings dirty flags.
 *
 * Returns the tab whose `dismissedNeedAttentionTabs` slice should be
 * written when the user clicks the global Save button, or `undefined`
 * when no dismissal should fire (active tab is Monitoring /
 * Authentication / undefined, OR the relevant dirty flag is false).
 */
export const deriveFooterDismissalTab = (
  irisConnectionActiveTab: IrisConnectionTab | undefined,
  dirty: {
    isDeviceSettingsDirty: boolean;
    isVideoSettingsDirty: boolean;
  }
): FooterDismissalTab | undefined => {
  if (irisConnectionActiveTab === 'video' && dirty.isVideoSettingsDirty) {
    return 'video';
  }
  if (irisConnectionActiveTab === 'control' && dirty.isDeviceSettingsDirty) {
    return 'control';
  }
  if (irisConnectionActiveTab === 'driver' && dirty.isDeviceSettingsDirty) {
    return 'driver';
  }
  return undefined;
};
